import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Plus, 
  Trash,
  Image as ImageIcon,
  PaperPlaneTilt
} from '@phosphor-icons/react';
import axios from 'axios';
import StickerImage from './StickerImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StickersPanel = ({ 
  onStickerSelect, 
  onClose, 
  userId,
  userRole = 'customer' // customer, handler, admin
}) => {
  const [customStickers, setCustomStickers] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedSticker, setSelectedSticker] = useState(null); // For send confirmation
  const fileInputRef = useRef(null);

  // Load custom stickers on mount
  useEffect(() => {
    loadCustomStickers();
  }, []);

  const loadCustomStickers = async () => {
    try {
      const response = await axios.get(`${API}/stickers`);
      setCustomStickers(response.data.stickers || []);
    } catch (error) {
      console.error('Failed to load stickers:', error);
      alert('Failed to load stickers. Please reopen the sticker panel to retry.');
    }
  };

  const handleStickerClick = (stickerUrl) => {
    // Show send confirmation instead of immediately sending
    setSelectedSticker(stickerUrl);
  };

  const handleSendSticker = () => {
    if (selectedSticker) {
      onStickerSelect(selectedSticker);
      onClose();
    }
  };

  const getStickerImageUrl = (sticker) => {
    // The stickers API returns an absolute URL from the storage service. Older
    // builds stored a bare "/uploads/..." path, so keep handling that shape too.
    if (!sticker?.url) return '';
    return sticker.url.startsWith('http') ? sticker.url : `${BACKEND_URL}${sticker.url}`;
  };

  const handleUploadSticker = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'].includes(file.type)) {
      alert('Please select a JPG, PNG, GIF, WebP or HEIC image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/stickers/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        loadCustomStickers();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload sticker. Please try again.');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteSticker = async (stickerId) => {
    if (!window.confirm('Delete this sticker?')) return;

    try {
      await axios.delete(`${API}/stickers/${stickerId}`);
      setSelectedSticker(null);
      loadCustomStickers();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete sticker');
    }
  };

  // Roles in this app are super_admin and *_handler; the old 'handler'/'admin'
  // literals never matched, so the upload control was hidden from every user.
  const canUpload = userRole === 'super_admin' || String(userRole || '').endsWith('_handler');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
      <div className="bg-wa-panel w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ImageIcon size={24} className="text-green-600" />
            <h3 className="font-semibold text-lg">Custom Stickers</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-wa-hover rounded-full">
            <X size={24} />
          </button>
        </div>

        {/* Stickers Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Upload Button - Only for Admin/Handler */}
          {canUpload && (
            <div className="mb-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic"
                onChange={handleUploadSticker}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-wa-primary-strong text-white rounded-xl hover:bg-wa-primary-strong disabled:opacity-50 transition-colors"
              >
                <Plus size={20} weight="bold" />
                {isUploading ? 'Uploading...' : 'Add Photo Sticker'}
              </button>
              <p className="mt-2 text-xs text-wa-text-secondary">
                {userRole === 'super_admin' ? 'Your stickers are shared with all handlers.' : 'Admin stickers are shared. Stickers you add are visible only in your library.'}
              </p>
            </div>
          )}

          {/* Custom Stickers Grid */}
          {customStickers.length > 0 ? (
            <div className="grid grid-cols-3 gap-3">
              {customStickers.map((sticker) => (
                <div key={sticker.id} className="relative group">
                  <button
                    onClick={() => handleStickerClick(getStickerImageUrl(sticker))}
                    className={`w-full h-[150px] flex items-center justify-center rounded-xl overflow-hidden transition-all ${
                      selectedSticker === getStickerImageUrl(sticker) 
                        ? 'ring-4 ring-green-500 scale-105' 
                        : 'hover:ring-2 ring-green-300'
                    }`}
                  >
                    <StickerImage
                      src={getStickerImageUrl(sticker)} 
                      alt="sticker"
                    />
                  </button>
                  {/* Delete button - only show for uploader */}
                  {canUpload && (sticker.created_by === userId || userRole === 'super_admin') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSticker(sticker.id);
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ImageIcon size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-wa-text-secondary mb-2">No custom stickers yet</p>
              {canUpload ? (
                <p className="text-sm text-wa-text-secondary">Click "Add Photo Sticker" to upload your first sticker</p>
              ) : (
                <p className="text-sm text-wa-text-secondary">Handlers will add stickers here</p>
              )}
            </div>
          )}
        </div>

        {/* Send Confirmation Bar - Shows when sticker is selected */}
        {selectedSticker && (
          <div className="border-t bg-wa-hover p-4 flex items-center gap-4">
            <div className="max-w-[150px] flex-shrink-0">
              <StickerImage
                src={selectedSticker} 
                alt="Selected sticker"
              />
            </div>
            <div className="flex-1">
              <p className="text-sm text-wa-text-secondary">Send this sticker?</p>
            </div>
            <button
              onClick={() => setSelectedSticker(null)}
              className="p-3 text-wa-text-secondary hover:bg-wa-hover rounded-full"
            >
              <X size={20} />
            </button>
            <button
              onClick={handleSendSticker}
              className="p-4 bg-wa-primary-strong text-white rounded-full hover:bg-wa-primary-strong shadow-lg"
            >
              <PaperPlaneTilt size={24} weight="fill" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickersPanel;
